<template>
  <div class="privacy-policy-container">
    <el-main>
      <div class="policy-content">
        <h1 class="policy-title">Privacy Policy</h1>
        <p class="policy-date">Last Updated: June 2025</p>
        <p class="policy-operator">Operated By: Extracurricular Activity Excellence International Pte. Ltd.</p>

        <section class="policy-section">
          <h2>1. Introduction</h2>
          <p>Extracurricular Activity Excellence International Pte. Ltd. ("we," "us," or "our") operates LearningFirst (the "Service"). This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our AI chat, social interactions, content creation, subscriptions, and other services. By using LearningFirst, you consent to these practices.</p>
        </section>

        <section class="policy-section">
          <h2>2. Information We Collect</h2>
          <p>We collect the following data:</p>
          
          <h3>A. Information You Provide</h3>
          <ul>
            <li><strong>Account Data:</strong> Email address (used as your account ID), OTP verification codes.</li>
            <li><strong>Profile Data:</strong> Display name, uploaded avatar, bio, and preferences.</li>
            <li><strong>User Content:</strong> Ideas, posts, AI chat interactions, messages, and content you create or share.</li>
            <li><strong>Payment Data:</strong> Transaction history, subscription details, and billing country (processed via third-party gateways like Stripe/PayPal). We do NOT store full payment card numbers.</li>
            <li><strong>Communications:</strong> Support inquiries or feedback.</li>
          </ul>

          <h3>B. Automatically Collected Data</h3>
          <ul>
            <li><strong>Usage Data:</strong> IP address, device type, browser, pages visited, interaction patterns, and timestamps.</li>
            <li><strong>Cookies/Tracking:</strong> We use cookies and similar technologies for functionality, analytics, and security. You can manage preferences via browser settings.</li>
          </ul>
        </section>

        <section class="policy-section">
          <h2>3. How We Use Your Information</h2>
          <p>We process your data to:</p>
          <ul>
            <li>Provide and maintain the Service (e.g., OTP logins, AI chat, content hosting).</li>
            <li>Facilitate user interactions (e.g., sharing content, profiles, subscriptions).</li>
            <li>Process payments and deliver purchased content.</li>
            <li>Improve the Service via data analytics and AI training (anonymized where possible).</li>
            <li>Secure accounts, prevent fraud, and enforce our Terms.</li>
            <li>Communicate updates, security alerts, or promotional offers (opt-out available).</li>
          </ul>
        </section>

        <section class="policy-section">
          <h2>4. Legal Basis for Processing (GDPR/Global)</h2>
          <p>We rely on:</p>
          <ul>
            <li><strong>Contractual Necessity:</strong> To deliver services you request (e.g., account creation, payments).</li>
            <li><strong>Legitimate Interests:</strong> Service optimization, security, and business operations.</li>
            <li><strong>Consent:</strong> For marketing emails or optional data uses (withdraw anytime).</li>
            <li><strong>Legal Obligations:</strong> To comply with Singaporean and international laws.</li>
          </ul>
        </section>

        <section class="policy-section">
          <h2>5. Data Sharing and Disclosure</h2>
          <p>Your data may be shared with:</p>
          
          <h3>Other Users</h3>
          <p>Your public profile, avatar, and shared content are visible to users.</p>
          
          <h3>Service Providers</h3>
          <p>Trusted partners for:</p>
          <ul>
            <li>OTP delivery (email/SMS providers).</li>
            <li>Cloud hosting and databases (servers in Singapore).</li>
            <li>Payment processing (PCI-compliant gateways).</li>
            <li>Analytics and AI infrastructure (e.g., AWS, Google Cloud).</li>
          </ul>
          
          <h3>Legal Authorities</h3>
          <p>If required by law (e.g., court orders, fraud investigations).</p>
          
          <h3>Business Transfers</h3>
          <p>In mergers, acquisitions, or asset sales (users will be notified).</p>
          
          <p><strong>We do NOT sell your personal data.</strong></p>
        </section>

        <section class="policy-section">
          <h2>6. International Data Transfers</h2>
          <p>All data is stored and processed in Singapore. If transfers occur outside Singapore (e.g., via third-party tools), we ensure safeguards like:</p>
          <ul>
            <li>Standard Contractual Clauses (SCCs).</li>
            <li>Adequacy decisions under GDPR.</li>
            <li>Provider compliance with global privacy frameworks.</li>
          </ul>
        </section>

        <section class="policy-section">
          <h2>7. Data Retention</h2>
          <p>We retain your data:</p>
          <ul>
            <li>While your account is active.</li>
            <li>For payment records (as required by law).</li>
            <li>For deleted accounts: Data is anonymized or deleted within 90 days, except where legal obligations require retention.</li>
          </ul>
        </section>

        <section class="policy-section">
          <h2>8. Your Rights (GDPR/CCPA/PIPEDA)</h2>
          <p>You may:</p>
          <ul>
            <li>Access, correct, or delete your personal data.</li>
            <li>Export your data (data portability).</li>
            <li>Withdraw consent (e.g., marketing emails).</li>
            <li>Object to processing or restrict use.</li>
            <li>Opt out of data sales (CCPA).</li>
            <li>Lodge complaints with supervisory authorities (e.g., Singapore's PDPC).</li>
          </ul>
          <p>To exercise rights, contact: <a href="mailto:contact@eae.international">contact@eae.international</a></p>
        </section>

        <section class="policy-section">
          <h2>9. Security Measures</h2>
          <p>We implement:</p>
          <ul>
            <li><strong>Encryption:</strong> Data in transit (TLS/SSL) and sensitive data at rest.</li>
            <li><strong>OTP Authentication:</strong> No passwords stored.</li>
            <li><strong>Access Controls:</strong> Strict employee/data access policies.</li>
            <li><strong>Regular Audits:</strong> Vulnerability assessments and penetration testing.</li>
          </ul>
        </section>

        <section class="policy-section">
          <h2>10. Children's Privacy</h2>
          <p>Our Service is designed to be accessible to students, including those under 18. In accordance with Singapore's Personal Data Protection Act (PDPA) and the PDPC Advisory Guidelines on Children's Personal Data in the Digital Environment, we apply the following safeguards:</p>
          <ul>
            <li><strong>Minimum Age:</strong> Users must be at least 13 years old to create an account. We do not knowingly collect personal data from children under 13.</li>
            <li><strong>Parental Consent (Under 13):</strong> If we become aware that a user is under 13, we will promptly delete their data unless verifiable parental or guardian consent has been obtained.</li>
            <li><strong>Parental Consent (13–17):</strong> Users aged 13 to 17 may use the Service with the consent of a parent or legal guardian. Parents or guardians who permit their child to use the Service agree to our Terms of Service and this Privacy Policy on the child's behalf.</li>
            <li><strong>Higher Standard of Care:</strong> We treat all personal data of users under 18 as sensitive and apply enhanced protective measures.</li>
            <li><strong>Data Minimisation:</strong> We limit the collection of minors' personal data to what is necessary for the Service. Minors' account information is not made publicly searchable by default.</li>
            <li><strong>Payments:</strong> Any purchases or subscriptions by users under 18 must be approved by a parent or legal guardian.</li>
          </ul>
          <p>If you believe your child's data has been collected without proper consent, please contact us at <a href="mailto:contact@eae.international">contact@eae.international</a> to request deletion.</p>
        </section>

        <section class="policy-section">
          <h2>11. Third-Party Links</h2>
          <p>LearningFirst may link to external sites (e.g., payment gateways). We are not responsible for their privacy practices.</p>
        </section>

        <section class="policy-section">
          <h2>12. Policy Updates</h2>
          <p>We will notify users of material changes via email or in-service alerts. Continued use constitutes acceptance.</p>
        </section>

        <section class="policy-section">
          <h2>13. Contact Us</h2>
          <p>For questions, data requests, or complaints:</p>
          <div class="contact-info">
            <p><strong>Extracurricular Activity Excellence International Pte. Ltd.</strong></p>
            <p>Email: <a href="mailto:contact@eae.international">contact@eae.international</a></p>
          </div>
        </section>
      </div>
    </el-main>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicy',
  meta: {
    title: 'Privacy Policy - LearningFirst'
  }
}
</script>

<style scoped>
.privacy-policy-container {
  width: 100%;
  min-height: 100vh;
  background-color: #f5f7fa;
  padding-top: 60px;
}

.policy-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  line-height: 1.6;
  color: #303133;
}

.policy-title {
  font-size: 2.5rem;
  font-weight: bold;
  color: #1159AF;
  margin-bottom: 10px;
  text-align: center;
}

.policy-date {
  font-size: 1.1rem;
  color: #606266;
  text-align: center;
  margin-bottom: 5px;
}

.policy-operator {
  font-size: 1.1rem;
  color: #606266;
  text-align: center;
  margin-bottom: 40px;
  font-weight: 500;
}

.policy-section {
  margin-bottom: 40px;
}

.policy-section h2 {
  font-size: 1.8rem;
  font-weight: bold;
  color: #1159AF;
  margin-bottom: 15px;
  border-bottom: 2px solid #e4e7ed;
  padding-bottom: 8px;
}

.policy-section h3 {
  font-size: 1.4rem;
  font-weight: 600;
  color: #303133;
  margin: 25px 0 15px 0;
}

.policy-section p {
  margin-bottom: 15px;
  font-size: 1rem;
  color: #606266;
}

.policy-section ul {
  margin: 15px 0;
  padding-left: 20px;
}

.policy-section li {
  margin-bottom: 8px;
  color: #606266;
}

.policy-section strong {
  color: #303133;
  font-weight: 600;
}

.policy-section a {
  color: #409EFF;
  text-decoration: none;
}

.policy-section a:hover {
  text-decoration: underline;
}

.contact-info {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 6px;
  border-left: 4px solid #409EFF;
  margin-top: 15px;
}

.contact-info p {
  margin-bottom: 8px;
}

@media (max-width: 768px) {
  .policy-content {
    padding: 20px 15px;
    margin: 0 10px;
  }
  
  .policy-title {
    font-size: 2rem;
  }
  
  .policy-section h2 {
    font-size: 1.5rem;
  }
  
  .policy-section h3 {
    font-size: 1.2rem;
  }
}
</style> 